import { getListPageExcelSchema } from '../../utils/list-page-excel/editor-model';
import {
  buildListPageWorkbook,
  LIST_EXCEL_FIRST_DATA_ROW,
  LIST_EXCEL_HEADER_ROW,
} from '../../utils/list-page-excel/workbook';
import { getListQuestionDescriptors } from '../../utils/list-question-utils';
import { fraudEnablerOptions } from '../pages/psfa/fraudEnablers';
import type { Page, PageList } from '../types';
import psfaConfig from './psfa';

const isListPage = (page: Page): page is PageList => page.pageType === 'list';
const getPageQuestions = (page: Page) => ('questions' in page ? page.questions || [] : []);
const pages = psfaConfig.sections.flatMap((section) =>
  section.subsections.flatMap((subsection) => subsection.pages),
);
const listPages = pages.filter(isListPage);

describe('PSFA list bulk upload', () => {
  it('enables user registration', () => {
    expect(psfaConfig.register).toBe(true);
  });

  it('is enabled and every current list field has an Excel mapping', () => {
    expect(psfaConfig.listBulkUpload).toBe(true);
    expect(psfaConfig.listBulkUploadMode).toBe('table-editor');

    const unsupported = listPages.flatMap((page) =>
      getListQuestionDescriptors(getPageQuestions(page))
        .filter(
          (descriptor) =>
            descriptor.kind === 'unsupported' ||
            ((descriptor.kind === 'list' || descriptor.kind === 'multi-list') &&
              descriptor.options.length === 0),
        )
        .map((descriptor) => `${page.name}: ${descriptor.question.name}`),
    );

    expect(unsupported).toEqual([]);
  });

  it('serializes an Excel template for every current PSFA list page', async () => {
    for (const page of listPages) {
      const workbook = await buildListPageWorkbook({
        title: page.title,
        pageName: page.name,
        questions: page.questions || [],
        answers: {},
        tenant: 'psfa',
      });
      const buffer = await workbook.xlsx.writeBuffer();
      expect(buffer.byteLength).toBeGreaterThan(0);
    }
  });

  it('uses Yes and No for every Covid 19 activity question', () => {
    const covidQuestions = pages.flatMap((page) =>
      getListQuestionDescriptors(getPageQuestions(page))
        .map(({ question }) => question)
        .filter(({ title }) => title === 'Is this related to Covid 19 activity?'),
    );

    expect(covidQuestions).toHaveLength(8);
    expect(covidQuestions.map(({ name }) => name)).not.toContain('prevented_upstream_c19');
    covidQuestions.forEach((question) => {
      expect(question.questionType).toBe('radio');
      expect(question.options).toEqual(['Yes', 'No']);
      expect(question.rules?.required).toBe(true);
    });
  });

  it('uses optional comment fields with hints without an optional title suffix', () => {
    const comments = pages.flatMap((page) =>
      getListQuestionDescriptors(getPageQuestions(page))
        .map(({ question }) => question)
        .filter(({ title }) => title === 'Comments'),
    );

    expect(comments.length).toBeGreaterThan(0);
    comments.forEach((question) => {
      expect(question.rules?.required).toBe(false);
      expect(question.hideOptionalLabel).toBe(true);
      expect(question.hint).toBeTruthy();
    });
  });

  it('exports optional fraud enabler selects without optional labels', async () => {
    const pagesWithFraudEnablers = listPages.filter((page) =>
      getListQuestionDescriptors(page.questions || []).some(({ question }) =>
        String(question.name).endsWith('_support_additional'),
      ),
    );

    expect(pagesWithFraudEnablers).toHaveLength(3);
    for (const page of pagesWithFraudEnablers) {
      const schema = getListPageExcelSchema(page.questions || []);
      const enablerColumns = schema.columns.filter(
        ({ descriptors }) => descriptors[0].question.title === 'What was the fraud enabler?',
      );
      expect(enablerColumns).toHaveLength(2);
      expect(enablerColumns.map(({ descriptors }) => descriptors[0].question.hint)).toEqual([
        undefined,
        'If multiple',
      ]);
      expect(enablerColumns[0].descriptors[0].question.rules?.required).toBe(false);
      expect(enablerColumns[1].descriptors[0].question.rules?.required).toBe(false);
      expect(enablerColumns[1].descriptors[0].question.excludeOptionsFrom).toEqual([
        {
          field: enablerColumns[0].descriptors[0].question.name,
          message: 'Select a different fraud enabler from the first selection.',
        },
      ]);
      enablerColumns.forEach(({ descriptors }) => {
        expect(descriptors[0].kind).toBe('list');
        expect(descriptors[0].question.questionType).toBe('select');
        expect(descriptors[0].question.hideOptionalLabel).toBe(true);
        expect(descriptors[0].options.map(({ label }) => label)).toEqual(fraudEnablerOptions);
      });

      const workbook = await buildListPageWorkbook({
        title: page.title,
        pageName: page.name,
        questions: page.questions || [],
        answers: {},
        tenant: 'psfa',
      });
      const worksheet = workbook.worksheets.find(({ name }) => name !== '__Lists');
      if (!worksheet) throw new Error('Expected PSFA fraud worksheet');
      const enablerColumnIndexes: number[] = [];
      worksheet.getRow(LIST_EXCEL_HEADER_ROW).eachCell((cell, columnIndex) => {
        if (cell.value === 'What was the fraud enabler?') enablerColumnIndexes.push(columnIndex);
      });
      expect(enablerColumnIndexes).toHaveLength(2);
      enablerColumnIndexes.forEach((columnIndex) => {
        expect(worksheet.getCell(LIST_EXCEL_FIRST_DATA_ROW, columnIndex).dataValidation.type).toBe(
          'list',
        );
        expect(
          worksheet.getCell(LIST_EXCEL_FIRST_DATA_ROW, columnIndex).dataValidation.allowBlank,
        ).toBe(true);
      });
    }
  });
});
